import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAccount } from "../../lib/queries/index.js";
import { PATHS } from "../../lib/routes.js";
import { usePathname } from "../../lib/url-filters.js";
import { useIntroSettled } from "../loader/index.js";
import { useTour } from "../tour/tour.js";
import { type Update, UPDATES } from "./updates.js";

// The modal, its art and its film arrive on first open; the rail card does not need them.
const NewsModal = lazy(async () => {
  const module = await import("./news-modal.js");
  return { default: module.NewsModal };
});

interface WhatsNew {
  readonly open: () => void;
  /** Updates newer than the newest this person has opened. */
  readonly unseen: number;
  readonly newest: Update | undefined;
}

const WhatsNewContext = createContext<WhatsNew | null>(null);

/** Null outside the signed-in shell, where there is no one to tell. */
export function useWhatsNew(): WhatsNew | null {
  return useContext(WhatsNewContext);
}

/** Wait after the intro lands, so the screen is seen before anything covers it. */
const SETTLE_MS = 900;

/**
 * What's new for one person (DESIGN.md §9, "What's new"): what they have not
 * seen, the modal, and the one time it opens by itself.
 */
export function WhatsNewProvider({
  userId,
  mayManage,
  children,
}: {
  readonly userId: string;
  /** An owner or admin, who may take an action only they can (connect an app). */
  readonly mayManage: boolean;
  readonly children: ReactNode;
}) {
  const [isOpen, setOpen] = useState(false);
  const [seenId, setSeenId] = useState(() => readSeen(userId));
  const newest = UPDATES[0];
  const seenAt = UPDATES.findIndex((update) => update.id === seenId);
  const unseen = seenAt === -1 ? UPDATES.length : seenAt;

  const open = () => {
    setOpen(true);
    if (newest !== undefined) {
      writeSeen(userId, newest.id);
      setSeenId(newest.id);
    }
  };

  useOpensOnArrival(unseen > 0, open);

  return (
    <WhatsNewContext.Provider value={{ open, unseen, newest }}>
      {children}
      {isOpen ? (
        <Suspense fallback={null}>
          <NewsModal
            updates={UPDATES}
            mayManage={mayManage}
            onClose={() => {
              setOpen(false);
            }}
          />
        </Suspense>
      ) : null}
    </WhatsNewContext.Provider>
  );
}

/**
 * Once, by itself: on whichever screen a person lands, after the intro has
 * landed, for someone who has finished the tour and has something they have
 * not seen. Never on setup, whose steps it would interrupt. A new account
 * never gets it — the tour is their introduction, and to them everything is
 * new.
 */
function useOpensOnArrival(hasUnseen: boolean, open: () => void): void {
  const path = usePathname();
  const settled = useIntroSettled();
  const tour = useTour();
  const tourSeen = useAccount().data?.tourSeen === true;
  const done = useRef(false);
  const ready =
    hasUnseen && settled && tourSeen && tour.at === null && path !== PATHS.newBrand;

  // A timer, not data: the modal waits a beat after the page arrives.
  useEffect(() => {
    if (!ready || done.current) return undefined;
    const timer = window.setTimeout(() => {
      done.current = true;
      open();
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  });
}

function storageKey(userId: string): string {
  return `crux.news.seen.${userId}`;
}

/** Remembered in this browser, as the newest update this person has opened. */
function readSeen(userId: string): string | null {
  return window.localStorage.getItem(storageKey(userId));
}

function writeSeen(userId: string, id: string): void {
  window.localStorage.setItem(storageKey(userId), id);
}
